import {
  Box,
  DataGrid,
  DataGridProps,
  gridClasses,
  GridColDef,
  GridPaginationModel,
  GridRowsProp,
  GridSortItem,
  GridSortModel,
  GridValidRowModel,
  SxProps,
} from "@bvhi/ui-base";
import Theme from "@bvhi/ui-theme/src/theme/Theme";
import { useCallback, useEffect, useState } from "react";
import { BVHIBounceDataGridLoader } from "../bvhi-bounce-data-grid-loader";
import { BVHIPagination } from "./bvhi-pagination";

type Props<T extends GridValidRowModel> = {
  initialSortModel?: GridSortItem;
  initalPaginationModel?: GridPaginationModel;
  rows: GridRowsProp<T>;
  columns: GridColDef<T>[];
  disableColumnMenu?: boolean;
  totalPages?: number;
  rowCount: number;
  updateCallback?: (paginationModel: GridPaginationModel, sortModel: GridSortModel) => void;
  styles?: SxProps<typeof Theme>;
} & Omit<DataGridProps, "columns" | "rows">;

export function BVHIDataGrid<T extends GridValidRowModel>({
  updateCallback,
  disableColumnMenu = true,
  columns,
  rows,
  rowCount,
  initalPaginationModel,
  styles,
  initialSortModel,
  ...props
}: Props<T>) {
  const [pageModel, setPageModel] = useState<GridPaginationModel>(
    initalPaginationModel ? initalPaginationModel : { page: 0, pageSize: 25 }
  );
  const [sortModel, setSortModel] = useState<GridSortModel>(initialSortModel ? [initialSortModel] : []);

  const onPaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      setPageModel(model);
    },
    [setPageModel]
  );

  const onSortModelChange = useCallback(
    (model: GridSortModel) => {
      setSortModel(model);
    },
    [setSortModel]
  );

  useEffect(() => {
    if (updateCallback) updateCallback(pageModel, sortModel);
  }, [sortModel, pageModel, updateCallback]);

  return (
    <Box sx={{ width: "100%", display: "grid" }}>
      <DataGrid
        columns={columns}
        disableColumnMenu={disableColumnMenu}
        disableRowSelectionOnClick
        hideFooterSelectedRowCount
        onPaginationModelChange={onPaginationModelChange}
        onSortModelChange={onSortModelChange}
        pageSizeOptions={[10, 25, 50]}
        paginationMode="server"
        paginationModel={pageModel}
        rowCount={rowCount}
        rows={rows}
        slots={{ loadingOverlay: BVHIBounceDataGridLoader, pagination: BVHIPagination }}
        sortingMode="server"
        sortModel={sortModel}
        sx={{
          minHeight: 300,
          border: "none",
          "& .MuiTablePagination-select": {
            padding: 1,
          },
          "& .datagrid-sticky-right": {
            position: "sticky",
            right: 0,
            background:
              "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.8) 10%, rgba(255,255,255,0.9) 25%, rgba(255,255,255,0.95) 50%, rgba(255,255,255,1) 100%)",
          },
          [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
            outline: "none",
          },
          [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
            outline: "none",
          },
          ...styles,
        }}
        {...props}
      />
    </Box>
  );
}
